import { forkJoin as observableForkJoin, Observable } from 'rxjs';
import { Component, OnInit, AfterViewInit, ViewChild, inject, ChangeDetectionStrategy } from '@angular/core';
import { ActivatedRoute, Params, Router } from '@angular/router';
import { LoadingPanelService, LoadingPanelSettings } from '../shared/loading-panel/loading-panel.service';
import { GenericModalService, ModalSettingsInput, ModalMultiButton, ModalSettingsMessage, ModalSettingsChangePassword } from '../shared/generic-modal/generic-modal.service';
import { ToastService, ToastType } from '../shared/toast/toast.service';
import { PartnerComponentBase } from './partner-search-details.base';
import { CurrentUserService } from '../user/services/current-user.service';
import { TagsService } from '../tags/services/tags.service';
import { PartnerTagsService } from './services/partner-tags.service';
import { Tag, TagsUsage } from '../tags/models';

import { FormsModule } from '@angular/forms';
import { PermissionRequiredDirective } from '../shared/authentication/permission-required.directive';

@Component({
    templateUrl: './partner-search-details-tags.component.html',
    selector: 'ep-partner-details-tags',
    changeDetection: ChangeDetectionStrategy.Eager,
    imports: [FormsModule, PermissionRequiredDirective]
})

export class PartnerSearchDetailsTagsComponent extends PartnerComponentBase {
    private tagsService = inject(TagsService);
    private partnerTagsService = inject(PartnerTagsService);
    private toastService = inject(ToastService);
    private loadingPanelService = inject(LoadingPanelService);

    public allTags: Tag[] = [];
    public allChecks: boolean[] = [];
    public isEditing: boolean = false;
    private originalChecks: boolean[];

    async partnerSet(): Promise<void> {
        await this.loadTags();
    }

    private async loadTags(): Promise<void> {
        const partnerId = this.selectedPartner?.PartnersId;

        const [allTags, partnerTags] = await Promise.all([
            this.tagsService.getTags(TagsUsage.Partner),
            partnerId ? this.partnerTagsService.getAllPartnerTags(partnerId) : []
        ]);

        this.allTags = allTags.sort((t1: Tag, t2: Tag) => t1.Name.localeCompare(t2.Name));
        const partnerTagIds = partnerTags.map(tag => tag.Id);
        this.allChecks = this.allTags.map(tag => partnerTagIds.includes(tag.Id));
    }

    private async saveTags(): Promise<void> {
        try {
            this.loadingPanelService.showLoadingPanelDirect('Saving tags...');

            const tagIds: number[] = this.allTags
                .filter((_, index) => this.allChecks[index])
                .map(tag => tag.Id)
                .sort((a, b) => a - b);
            await this.partnerTagsService.setAllPartnerTags(this.selectedPartner.PartnersId, tagIds);
        }
        catch {
            this.toastService.showToast('Failed to save tags...', ToastType.error);
        }
        finally {
            this.loadingPanelService.hideLoadingPanel();
            this.toastService.showToast('Tags saved...', ToastType.success);
        }
    }

    edit(): void {
        this.isEditing = true;
        this.setEditMode(this.isEditing);
        this.originalChecks = Object.assign([], this.allChecks);
    }

    save(): void {
        this.isEditing = false;
        this.setEditMode(this.isEditing);
        void this.saveTags();
    }

    cancel(): void {
        this.allChecks = this.originalChecks;
        this.isEditing = false;
        this.setEditMode(this.isEditing);
    }
}
